/**
 * Gaussian-splat viewer using `@mkkellogg/gaussian-splats-3d`. The library
 * owns its own Three.js scene and animation loop; we just mount its DOM and
 * swap the loaded splat whenever a new one is produced.
 */
import { Viewer } from "@mkkellogg/gaussian-splats-3d";

const SCENE_FORMAT_SPLAT = 0;  // matches SceneFormat.Splat in the lib

let viewer: Viewer | null = null;
let currentUrl: string | null = null;
let sceneCount = 0;

function ensureViewer(host: HTMLElement): Viewer {
  if (viewer) return viewer;
  viewer = new Viewer({
    rootElement: host,
    // Our splat bytes follow the upstream Python TRANSFORM convention
    // (Y-down in the saved data), so they're compatible with SparkJS and
    // SuperSplat. mkkellogg's viewer interprets +Y as up by default, which
    // would render upside down -- flip cameraUp instead of touching the
    // bytes, so the saved .splat stays portable.
    cameraUp: [0, -1, 0],
    initialCameraPosition: [0, 0, 2.0],
    initialCameraLookAt: [0, 0, 0],
    sharedMemoryForWorkers: false,
    selfDrivenMode: true,
    useBuiltInControls: true,
  });
  viewer.start();
  return viewer;
}

export async function setSplat(host: HTMLElement, buf: ArrayBuffer) {
  const v = ensureViewer(host);
  // Dispose the previously loaded splat (if any) so the viewer doesn't keep
  // stacking scenes and growing its layout on each Generate.
  while (sceneCount > 0) {
    try { await (v as any).removeSplatScene(0); } catch { break; }
    sceneCount--;
  }
  if (currentUrl) { URL.revokeObjectURL(currentUrl); currentUrl = null; }
  currentUrl = URL.createObjectURL(new Blob([buf], { type: "application/octet-stream" }));
  await v.addSplatScene(currentUrl, {
    format: SCENE_FORMAT_SPLAT,
    showLoadingUI: false,
    splatAlphaRemovalThreshold: 1,
  });
  sceneCount++;
}
